'use client';

export interface BrowserNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  sound?: boolean;
  soundType?: 'message' | 'mention' | 'call';
  tag?: string;
  requireInteraction?: boolean;
}

export class ClientNotificationService {
  private static instance: ClientNotificationService;
  private isInitialized = false;
  private permission: NotificationPermission = 'default';
  private audioContext: AudioContext | null = null;
  private notificationSounds: { [key: string]: AudioBuffer | null } = {
    message: null,
    mention: null,
    call: null
  };
  
  private constructor() {
    if (typeof window !== 'undefined') {
      this.initializeNotifications();
      this.initializeAudio();
    }
  }
  
  public static getInstance(): ClientNotificationService {
    if (!ClientNotificationService.instance) {
      ClientNotificationService.instance = new ClientNotificationService();
    }
    return ClientNotificationService.instance;
  }
  
  private async initializeNotifications() {
    if (typeof window === 'undefined') return;
    if ('Notification' in window) {
      this.permission = Notification.permission;
      
      if (this.permission === 'default') {
        this.permission = await Notification.requestPermission();
      }
      
      this.isInitialized = true;
    }
  }
  
  private async initializeAudio() {
    if (typeof window === 'undefined') return;
    try {
      // Initialize audio context on user interaction
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      await this.loadNotificationSounds();
    } catch (error) {
      console.warn('Audio context initialization failed:', error);
    }
  }
  
  private async loadNotificationSounds() {
    if (!this.audioContext) return;
    
    // Create simple notification sounds programmatically
    const createBeepSound = (frequency: number, duration: number): AudioBuffer => {
      const sampleRate = this.audioContext!.sampleRate;
      const buffer = this.audioContext!.createBuffer(1, sampleRate * duration, sampleRate);
      const channelData = buffer.getChannelData(0);
      
      for (let i = 0; i < buffer.length; i++) {
        const time = i / sampleRate;
        channelData[i] = Math.sin(2 * Math.PI * frequency * time) * 0.3 * Math.exp(-time * 2);
      }
      
      return buffer;
    };
    
    try {
      this.notificationSounds.message = createBeepSound(800, 0.3);
      this.notificationSounds.mention = createBeepSound(1000, 0.4);
      this.notificationSounds.call = createBeepSound(600, 0.6);
    } catch (error) {
      console.warn('Failed to create notification sounds:', error);
    }
  }
  
  public async requestPermission(): Promise<NotificationPermission> {
    if (!this.isInitialized) return 'denied';
    
    this.permission = await Notification.requestPermission();
    return this.permission;
  }
  
  public getPermission(): NotificationPermission {
    return this.permission;
  }
  
  public async showNotification(options: BrowserNotificationOptions): Promise<Notification | null> {
    if (!this.isInitialized || this.permission !== 'granted') {
      return null;
    }
    
    try {
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/favicon.ico',
        tag: options.tag,
        requireInteraction: options.requireInteraction || false,
        silent: !options.sound
      });
      
      // Play notification sound if enabled
      if (options.sound !== false) {
        await this.playNotificationSound(options.soundType || 'message');
      }
      
      return notification;
    } catch (error) {
      console.error('Failed to show notification:', error);
      return null;
    }
  }
  
  public async playNotificationSound(type: 'message' | 'mention' | 'call' = 'message') {
    if (!this.audioContext || !this.notificationSounds[type]) return;
    
    try {
      // Resume audio context if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      const source = this.audioContext.createBufferSource();
      source.buffer = this.notificationSounds[type];
      source.connect(this.audioContext.destination);
      source.start();
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
    }
  }
  
  public enableSounds() {
    // This method can be called after user interaction to enable audio context
    if (this.audioContext && this.audioContext.state === 'suspended') {
      this.audioContext.resume();
    }
  }
  
  public async showChatMessage(senderName: string, message: string, avatar?: string) {
    return this.showNotification({
      title: `Message from ${senderName}`,
      body: message,
      icon: avatar || '/default-avatar.png',
      sound: true,
      soundType: 'message',
      tag: 'chat-message',
      requireInteraction: false
    });
  }
  
  public async showMentionNotification(senderName: string, message: string, avatar?: string) {
    return this.showNotification({
      title: `${senderName} mentioned you`,
      body: message,
      icon: avatar || '/default-avatar.png',
      sound: true,
      soundType: 'mention',
      tag: 'chat-mention',
      requireInteraction: true
    });
  }
  
  public async showTypingNotification(userName: string) {
    // This is typically handled in the UI, not as browser notification
    // But we can trigger a subtle sound
    await this.playNotificationSound('message');
  }
}

export default ClientNotificationService;